import { DecimalPipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { environment } from '@environments/environment'
import { firstValueFrom, forkJoin, from } from 'rxjs'
import { ToastrService } from '../../../../../../services/toastr.service'

interface UploadedFile {
  name: string
  url: string
  isCopied?: boolean
  extension: string
}

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  standalone: true,
  imports: [DecimalPipe]
})
export class FileUploaderComponent implements OnInit, OnDestroy {
  selectedFiles: File[] = []
  uploadedFiles: UploadedFile[] = []
  isUploading = false
  uploadSuccess = false
  uploadError = ''

  private filePreviews: Map<File, { raw: string; safe: SafeUrl }> = new Map()
  private readonly ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
  private readonly _destroyRef = inject(DestroyRef)

  constructor(private http: HttpClient, private toastr: ToastrService, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    // Initialize component
  }

  ngOnDestroy(): void {
    this.clearAllPreviews()
  }

  /**
   * Handles file selection from input or drag-drop
   */
  public onFileSelected(event: Event, append: boolean = false): void {
    const input = event.target as HTMLInputElement
    if (!input.files || input.files.length === 0) return

    const files = Array.from(input.files)
    const validFiles = files.filter((file) => this.ALLOWED_IMAGE_TYPES.includes(file.type))

    if (validFiles.length !== input.files.length) {
      this.toastr.error('Error', 'Only image files are allowed (JPG, JPEG, PNG, PDF)')
    }

    if (validFiles.length > 0) {
      if (append) {
        this.selectedFiles = [...this.selectedFiles, ...validFiles]
      } else {
        this.selectedFiles = validFiles
      }
      this.uploadError = ''
      this.uploadSuccess = false
    }

    input.value = ''
  }

  /**
   * Handles drag and drop events - click to open file picker
   */
  public onDropZoneClick(event: Event): void {
    const target = event.target as HTMLElement
    const dropZone = event.currentTarget as HTMLElement

    if (this.selectedFiles.length === 0 && (target === dropZone || target.closest('.upload-content'))) {
      const fileInput = document.querySelector('#mainFileInput') as HTMLInputElement
      if (fileInput) {
        fileInput.click()
      }
    }
  }

  /**
   * Handles drag over event to allow drop
   * @param event DragEvent
   */
  public onDragOver(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  /**
   * Handles drag leave event
   * @param event DragEvent
   */
  public onDragLeave(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Handles drop event to process dropped files
   * @param event DragEvent
   */
  public onDrop(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()

    if (!event.dataTransfer || !event.dataTransfer.files) return

    const files = Array.from(event.dataTransfer.files)
    const validFiles = files.filter((file) => this.ALLOWED_IMAGE_TYPES.includes(file.type))

    if (validFiles.length !== files.length) {
      this.toastr.error('Error', 'Only image files are allowed (JPG, JPEG, PNG, PDF)')
    }

    if (validFiles.length > 0) {
      this.selectedFiles = [...this.selectedFiles, ...validFiles]
      this.uploadError = ''
      this.uploadSuccess = false
    }
  }

  /**
   * Adds more files to the current selection
   */
  public addMoreFiles(event: Event): void {
    event.stopPropagation()
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.multiple = true
    fileInput.accept = 'image/*, application/pdf'
    fileInput.style.display = 'none'

    fileInput.onchange = (e) => {
      this.onFileSelected(e, true)
      document.body.removeChild(fileInput)
    }

    document.body.appendChild(fileInput)
    fileInput.click()
  }

  /**
   * Removes a file from the selection
   */
  public removeFile(index: number, event?: Event): void {
    if (event) {
      event.stopPropagation()
    }

    const fileToRemove = this.selectedFiles[index]
    if (fileToRemove) {
      const url = this.filePreviews.get(fileToRemove)
      if (url) {
        URL.revokeObjectURL(url.raw)
        this.filePreviews.delete(fileToRemove)
      }
    }

    this.selectedFiles.splice(index, 1)
    if (this.selectedFiles.length === 0) {
      this.uploadError = ''
    }
  }

  /**
   * Clears all selected files
   */
  public clearAllFiles(): void {
    this.selectedFiles.forEach((file) => {
      const url = this.filePreviews.get(file)
      if (url) {
        URL.revokeObjectURL(url.raw)
        this.filePreviews.delete(file)
      }
    })
    this.selectedFiles = []
    this.uploadError = ''
  }

  /**
   * Gets a safe preview URL for a file
   */
  public getFilePreview(file: File): SafeUrl {
    if (!file || file.size === 0) {
      console.warn('Invalid file provided to getFilePreview:', file)
      return this.sanitizer.bypassSecurityTrustUrl('')
    }

    if (this.filePreviews.has(file)) {
      return this.filePreviews.get(file)!.safe
    }

    try {
      const blobUrl = URL.createObjectURL(file)
      this.filePreviews.set(file, {
        raw: blobUrl,
        safe: this.sanitizer.bypassSecurityTrustUrl(blobUrl)
      })
      return this.filePreviews.get(file)!.safe
    } catch (error) {
      console.error('Error creating blob URL for file:', file.name, error)
      return this.sanitizer.bypassSecurityTrustUrl('')
    }
  }

  /**
   * Uploads selected files to the server
   */
  public async uploadFiles(): Promise<void> {
    if (this.selectedFiles.length === 0) return

    this.isUploading = true
    this.uploadError = ''
    this.uploadSuccess = false

    const uploadPromises = this.selectedFiles.map((file) => this._uploadSingleFile(file))
    const uploadObservables = uploadPromises.map((promise) => from(promise))

    try {
      const results = await firstValueFrom(forkJoin(uploadObservables))
      this.uploadedFiles.push(...results)
      this.uploadSuccess = true
      this.toastr.success('Success', `${results.length} file(s) uploaded successfully`)

      // Clean up blob URLs for uploaded files
      this.selectedFiles.forEach((file) => {
        const url = this.filePreviews.get(file)
        if (url) {
          URL.revokeObjectURL(url.raw)
          this.filePreviews.delete(file)
        }
      })

      this.selectedFiles = []
    } catch (error) {
      console.error('Upload error:', error)
      this.uploadError = 'Failed to upload one or more files'
      this.toastr.error('Error', this.uploadError)
    } finally {
      this.isUploading = false
    }
  }

  /**
   * Uploads a single file
   */
  private _uploadSingleFile(file: File): Promise<UploadedFile> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('file', file)

      this.http
        .post<{ data: { uploads: { name: string; id: number; path_url: string; extension: string } }; message: string }>(`${environment.v2.baseUrl}generic/uploads`, formData)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (response) => {
            const fileName = response.data.uploads.name
            const fileUrl = response.data.uploads.path_url.replace('lqip/', '').replace('-lqip', '')
            resolve({
              name: fileName,
              url: fileUrl,
              extension: response.data.uploads.extension
            })
          },
          error: (error) => {
            console.error('Error uploading file:', file.name, error)
            reject(error)
          }
        })
    })
  }

  /**
   * Copies a file URL to clipboard
   */
  public async copyFileUrl(url: string, index: number): Promise<void> {
    try {
      await navigator.clipboard.writeText(url)
      this.uploadedFiles[index].isCopied = true
      setTimeout(() => {
        this.uploadedFiles[index].isCopied = false
      }, 2000)
      this.toastr.success('Success', 'URL copied to clipboard')
    } catch (error) {
      this.toastr.error('Error', 'Failed to copy URL to clipboard')
    }
  }

  /**
   * Clears all blob URL previews
   */
  private clearAllPreviews(): void {
    this.filePreviews.forEach((entry) => {
      URL.revokeObjectURL(entry.raw)
    })
    this.filePreviews.clear()
  }
}
